import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Container, GoldButton } from "@/components/shared";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Laid on the harbour master's desk.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <PageHero
        kicker="Contact"
        title="Write to the harbour master"
        intro="Questions, corrections, or a word about the crossing — send them here. In this preview, messages are stored; in service, they would reach info@madeinboston.co.uk."
      />

      <section className="py-16 sm:py-24">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="w-12 h-12 border border-[#CDA434] flex items-center justify-center text-[#CDA434] mb-6"><Mail size={22} /></div>
              <h2 className="font-serif text-2xl text-[#1E1E2F] mb-4">By post or by wire</h2>
              <div className="space-y-4 font-sans text-base text-[#1B2430]">
                <p><span className="text-[#505A68] text-sm uppercase tracking-wide block mb-1">Email</span><a href="mailto:info@madeinboston.co.uk" className="text-[#003366] link-underline">info@madeinboston.co.uk</a></p>
                <p><span className="text-[#505A68] text-sm uppercase tracking-wide block mb-1">Press</span><a href="mailto:press@madeinboston.co.uk" className="text-[#003366] link-underline">press@madeinboston.co.uk</a></p>
                <p><span className="text-[#505A68] text-sm uppercase tracking-wide block mb-1">Correspondence only</span>75 High Street, Boston, Lincolnshire</p>
              </div>
            </div>

            <div className="lg:col-span-7">
              {sent ? (
                <div data-testid="contact-success" className="bg-[#FFFAF3] border border-[#CDA434] p-10 text-center">
                  <p className="font-playfair italic text-xl text-[#7a611c] mb-3">Laid on the harbour master's desk.</p>
                  <p className="font-sans text-sm text-[#505A68]">Thank you. In service, this note would reach info@madeinboston.co.uk.</p>
                  <button onClick={() => { setSent(false); setForm({ name: "", email: "", message: "" }); }} className="mt-5 font-sans text-sm font-semibold text-[#003366] link-underline">Send another</button>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-[#FFFAF3] border border-[#D6D3CB] p-8 space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Your name</label>
                      <input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Your email</label>
                      <input required type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Message</label>
                    <textarea required rows={5} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} />
                  </div>
                  <GoldButton type="submit" disabled={sending} data-testid="contact-submit" className="w-full">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : "Send message"}
                  </GoldButton>
                </form>
              )}
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-[#FFFAF3] border border-[#D6D3CB] focus:border-[#003366] px-4 py-2.5 text-sm font-sans text-[#1B2430]";
const labelCls = "block font-sans text-xs uppercase tracking-wider font-semibold text-[#1B2430] mb-1.5";
